import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Toaster } from "@/components/ui/sonner";
import { Spinner } from "@/components/ui/spinner";
import { environment } from "@/config/env";
import { useCart } from "@/contexts/CartContext";
import {
  useEliteReservation,
  useEliteStructure,
} from "@/contexts/EliteTemporaryContextProvider";
import axios from "axios";
import {
  ArrowLeft,
  Clock,
  Info,
  MapPin,
  Minus,
  Plus,
  ShoppingCart,
  Sparkles,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { getTranslatedName as utilsGetTranslatedName } from "@/lib/utils";

export default function ServiceDetail() {
  const { isMobile } = useIsMobile();
  const routeParams = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { structure } = useEliteStructure();
  const { reservation } = useEliteReservation();
  const { t, i18n } = useTranslation("common");
  const [service, setService] = useState<any>(null);

  const serviceName = service ? getTranslatedName(service.name) : "";
  useDocumentTitle(serviceName || "Dettaglio Servizio");

  const [subserviceCategoryArr, setSubserviceCategoryArr] = useState<any[]>([]);
  const [selectedSubServiceId, setSelectedSubServiceId] = useState<
    string | number | null
  >(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [quantityService, setQuantityService] = useState<number>(1);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const { addItem, items } = useCart();

  const days = [
    { key: "monday", label: t("days.monday") },
    { key: "tuesday", label: t("days.tuesday") },
    { key: "wednesday", label: t("days.wednesday") },
    { key: "thursday", label: t("days.thursday") },
    { key: "friday", label: t("days.friday") },
    { key: "saturday", label: t("days.saturday") },
    { key: "sunday", label: t("days.sunday") },
  ];

  function getServiceDetail(serviceId: string) {
    axios.defaults.baseURL = environment.ELITE_BASE_URI;
    axios
      .get(`concierge/${structure.id}/service/${serviceId}`)
      .then((response) => {
        let serviceData = response.data || [];
        setService(serviceData);

        const subServices =
          Array.isArray(serviceData.subServices) &&
          serviceData.isUniquePrice === false
            ? serviceData.subServices
            : [];
        const activeSubServices = subServices.filter(
          (sub: any) => sub?.isActive === true,
        );

        const subserviceCatArr: any[] = [];
        activeSubServices.forEach((sub: any) => {
          const category = sub.subServiceCategory;
          if (
            category?.id &&
            !subserviceCatArr.some((cat) => cat.id == category.id)
          ) {
            subserviceCatArr.push({
              id: String(category.id),
              description: category.description,
            });
          }
        });

        setSubserviceCategoryArr(subserviceCatArr);
        setSelectedCategoryId(
          subserviceCatArr.length > 0 ? String(subserviceCatArr[0].id) : null,
        );
      })
      .catch(() => {
        navigate("/expired", { replace: true });
      });
  }

  useEffect(() => {
    if (structure?.id) {
      const serviceId = routeParams.id;
      if (serviceId) {
        getServiceDetail(serviceId);
      }
    }
  }, [routeParams.id, navigate, structure?.id]);

  function getSubServiceId(subService: any, index: number) {
    return subService?.id ?? subService?.subServiceId ?? index;
  }

  function handleBack() {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
    } else {
      navigate("/services", { replace: true });
    }
  }

  function increment(subServiceId: string | number) {
    const key = String(subServiceId);
    setQuantities((prev) => ({ ...prev, [key]: (prev[key] ?? 1) + 1 }));
  }

  function decrement(subServiceId: string | number) {
    const key = String(subServiceId);
    setQuantities((prev) => ({
      ...prev,
      [key]: Math.max(1, (prev[key] ?? 1) - 1),
    }));
  }

  useEffect(() => {
    const subServices = Array.isArray(service?.subServices)
      ? service.subServices
      : [];
    if (subServices.length === 0) {
      setSelectedSubServiceId(null);
      return;
    }

    const firstId = getSubServiceId(subServices[0], 0);
    setSelectedSubServiceId((prev) => prev ?? firstId);
  }, [service]);

  function getTranslatedName(value: any): string {
    return utilsGetTranslatedName(value, i18n.language);
  }

  function toPascalCase(value: string) {
    return value
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join("");
  }

  function normalizeBase64Image(value: unknown): string | undefined {
    if (typeof value !== "string") return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (
      trimmed.startsWith("data:image/") ||
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("/")
    ) {
      return trimmed;
    }
    if (/^[A-Za-z0-9+/=\r\n]+$/.test(trimmed)) {
      return `data:image/png;base64,${trimmed.replace(/\s+/g, "")}`;
    }
    return undefined;
  }

  if (!service) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Spinner className="size-10 text-primary" />
      </div>
    );
  }

  function getLocalizedCover(
    cover: unknown,
    currentLang: string,
  ): string | undefined {
    if (!cover) return undefined;
    if (typeof cover === "object" && cover !== null) {
      const langKey = currentLang === "en" ? "gb" : currentLang;
      const val =
        (cover as any)[langKey] ||
        (cover as any)[currentLang] ||
        (cover as any)["it"] ||
        Object.values(cover)[0];
      return typeof val === "string" && val.trim() ? val.trim() : undefined;
    }
    if (typeof cover !== "string" || !cover.trim()) return undefined;
    const trimmed = cover.trim();
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === "object") {
          const langKey = currentLang === "en" ? "gb" : currentLang;
          const val =
            parsed[langKey] ||
            parsed[currentLang] ||
            parsed["it"] ||
            Object.values(parsed)[0];
          return typeof val === "string" && val.trim() ? val.trim() : undefined;
        }
      } catch {
        return undefined;
      }
    }
    return trimmed;
  }

  const customIconSrc = normalizeBase64Image(service?.customIcon);
  const activeCover = getLocalizedCover(service?.cover, i18n.language);
  const rawIconName = service?.icon || service?.defaultIcon;
  const iconByName =
    typeof rawIconName === "string" && rawIconName.trim()
      ? (LucideIcons[toPascalCase(rawIconName) as keyof typeof LucideIcons] as
          | LucideIcon
          | undefined)
      : undefined;
  const ServiceIcon =
    typeof service?.icon === "function"
      ? service.icon
      : iconByName || (rawIconName ? LucideIcons.ConciergeBell : undefined);

  const images: string[] =
    Array.isArray(service.serviceImages) && service.serviceImages.length > 0
      ? service.serviceImages
      : activeCover
        ? [activeCover]
        : [];

  const locationString = [service.address, service.city]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="min-h-screen pb-16 pt-2 px-3 sm:px-6 lg:px-8 max-w-6xl mx-auto">
      {/* Back Button */}
      <div className="mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="gap-2 text-muted-foreground hover:text-foreground pl-0"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("back") !== "back" ? t("back") : "Indietro"}
        </Button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header Title Section */}
        <div className="mb-6 space-y-2">
          <div className="flex items-center gap-3">
            {customIconSrc ? (
              <img
                src={customIconSrc}
                alt="Icon"
                className="w-9 h-9 object-contain rounded-lg shrink-0"
              />
            ) : ServiceIcon ? (
              <ServiceIcon className="w-8 h-8 text-primary shrink-0" />
            ) : null}
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground break-words [overflow-wrap:anywhere]">
              {getTranslatedName(service.name)}
            </h1>
          </div>

          {locationString && (
            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-primary shrink-0" />
              {locationString}
            </p>
          )}
        </div>

        {/* Hero Image Carousel */}
        {images.length > 0 && (
          <div className="mb-8 rounded-2xl overflow-hidden shadow-sm border bg-card">
            <Carousel
              autoPlay={true}
              opts={{ loop: true }}
              className="w-full relative"
            >
              <CarouselContent>
                {images.map((src: any, i: number) => (
                  <CarouselItem key={i}>
                    <div className="w-full h-56 sm:h-72 md:h-96">
                      <img
                        src={typeof src === "string" ? src : src.image}
                        alt={`${service.name} ${i + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {images.length > 1 && (
                <>
                  <CarouselPrevious className="left-3 bg-background/80 backdrop-blur-sm" />
                  <CarouselNext className="right-3 bg-background/80 backdrop-blur-sm" />
                </>
              )}
            </Carousel>
          </div>
        )}

        {/* 2-Column Responsive Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start min-w-0">
          {/* Left Column: Description & Subservices */}
          <div className="lg:col-span-2 space-y-8 min-w-0">
            {/* Description */}
            {service.description && (
              <Card className="p-6 shadow-sm border border-border/60 overflow-hidden min-w-0">
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2 text-foreground">
                  <Info className="h-5 w-5 text-primary shrink-0" />
                  {t("description") || "Descrizione"}
                </h2>
                <div
                  className="prose dark:prose-invert max-w-none text-muted-foreground leading-relaxed text-sm md:text-base break-words [overflow-wrap:anywhere] [word-break:break-word] whitespace-normal overflow-hidden"
                  dangerouslySetInnerHTML={{
                    __html: getTranslatedName(service.description),
                  }}
                />
              </Card>
            )}

            {/* Subservices List (if not unique price) */}
            {Array.isArray(service.subServices) &&
              service.isUniquePrice === false &&
              service.subServices.length > 0 && (
                <div className="space-y-4 min-w-0">
                  <h2 className="text-xl font-bold tracking-tight text-foreground">
                    {t("available_services") !== "available_services"
                      ? t("available_services")
                      : "Servizi disponibili"}
                  </h2>

                  {/* Subservice Category Tabs */}
                  {subserviceCategoryArr.length > 0 && (
                    <Tabs
                      value={selectedCategoryId ?? undefined}
                      onValueChange={(value) => setSelectedCategoryId(value)}
                      className="w-full"
                    >
                      <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 bg-muted/60">
                        {subserviceCategoryArr.map((category: any) => (
                          <TabsTrigger
                            key={String(category.id)}
                            value={String(category.id)}
                            className="px-4 py-2 text-sm font-medium"
                          >
                            {getTranslatedName(category.description)}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                    </Tabs>
                  )}

                  {/* Subservice Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {service.subServices
                      .filter((sub: any) => sub?.isActive === true)
                      .map((subService: any, index: number) => {
                        const subServiceId = getSubServiceId(subService, index);
                        const quantity = quantities[String(subServiceId)] ?? 1;
                        const subServiceCategoryId = String(
                          subService?.subServiceCategory?.id ?? "",
                        );

                        if (
                          selectedCategoryId &&
                          subServiceCategoryId !== String(selectedCategoryId) // se è informativo non prenotabile
                        ) {
                          return null;
                        }

                        return (
                          <Card
                            key={String(subServiceId)}
                            className="flex flex-col justify-between p-5 border border-border/60 hover:border-primary/40 transition-all shadow-sm min-w-0 overflow-hidden"
                          >
                            <div className="space-y-2 min-w-0">
                              <div className="flex justify-between items-start gap-2 min-w-0">
                                <h3 className="font-semibold text-base text-foreground break-words [overflow-wrap:anywhere]">
                                  {getTranslatedName(subService.name) || "-"}
                                </h3>
                                {(subService.price || subService.offer) && (
                                  <div className="text-right shrink-0">
                                    {subService.offer != null &&
                                    subService.offer !== "" &&
                                    subService.offer !== 0 ? (
                                      <div className="flex flex-col items-end">
                                        <span className="text-xs line-through text-muted-foreground">
                                          € {subService.price}
                                        </span>
                                        <span className="text-base font-bold text-primary">
                                          € {subService.offer}
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-base font-bold text-foreground">
                                        € {subService.price}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>

                              {subService.description && (
                                <p className="text-xs md:text-sm text-muted-foreground line-clamp-3 break-words [overflow-wrap:anywhere]">
                                  {getTranslatedName(subService.description)}
                                </p>
                              )}
                            </div>

                            <div className="pt-4 mt-4 space-y-3">
                              {service.serviceType === 1 && (
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-muted-foreground font-medium">
                                    {t("number_pieces") || "Quantità"}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="h-7 w-7"
                                      type="button"
                                      onClick={() => decrement(subServiceId)}
                                    >
                                      <Minus className="h-3 w-3" />
                                    </Button>
                                    <span className="w-6 text-center text-xs font-semibold">
                                      {quantity}
                                    </span>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="h-7 w-7"
                                      type="button"
                                      onClick={() => increment(subServiceId)}
                                    >
                                      <Plus className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              )}

                              {service.serviceType != 2 && (
                                <Button
                                  type="button"
                                  disabled={
                                    service.maxDailyQuantity != null &&
                                    items
                                      .filter(
                                        (i) =>
                                          String(i.serviceId) ===
                                          String(service.id),
                                      )
                                      .reduce(
                                        (acc, i) => acc + i.quantity,
                                        0,
                                      ) >= service.maxDailyQuantity
                                  }
                                  onClick={() => {
                                    for (let i = 0; i < quantity; i++) {
                                      addItem({
                                        id: subServiceId,
                                        serviceId: service.id,
                                        subServiceId: String(subServiceId),
                                        name: subService.name,
                                        price: Number(
                                          subService.offer || subService.price,
                                        ),
                                        image:
                                          subService.cover || service.cover,
                                        reservation: reservation?.reservation,
                                        isQuantityFixed:
                                          service.serviceType === 0,
                                        data: service.serviceAvailabilityData,
                                        serviceAvailability:
                                          service.serviceAvailability,
                                        maxDailyQuantity:
                                          service.maxDailyQuantity,
                                      });
                                    }
                                  }}
                                  className="w-full gap-2 text-xs md:text-sm"
                                >
                                  <ShoppingCart className="h-4 w-4" />
                                  {t("add_cart") || "Aggiungi al carrello"}
                                </Button>
                              )}
                            </div>
                          </Card>
                        );
                      })}
                  </div>
                </div>
              )}
          </div>

          {/* Right Column: Pricing Box & Info / Hours */}
          <div className="space-y-6 min-w-0">
            {/* Unique Price Booking Card */}
            {service.isUniquePrice === true && (
              <Card className="p-6 border-primary/30 shadow-md sticky top-20 bg-card min-w-0">
                <h3 className="font-semibold text-lg mb-4 flex items-center justify-between border-b pb-3">
                  <span>
                    {t("booking") !== "booking" ? t("booking") : "Prenotazione"}
                  </span>
                  <Badge variant="secondary" className="font-normal text-xs">
                    {t("unique_price") !== "unique_price"
                      ? t("unique_price")
                      : "Prezzo unico"}
                  </Badge>
                </h3>

                <div className="space-y-5">
                  {/* Price Banner */}
                  <div className="bg-muted/40 p-4 rounded-xl flex items-center justify-between">
                    <span className="text-sm text-muted-foreground font-medium">
                      {t("price") || "Prezzo"}
                    </span>
                    {service.offer != null &&
                    service.offer !== "" &&
                    service.price1 ? (
                      <div className="text-right">
                        <span className="text-xs line-through text-muted-foreground mr-2">
                          € {service.price1}
                        </span>
                        <span className="text-2xl font-bold text-primary">
                          € {service.offer}
                        </span>
                      </div>
                    ) : (
                      <span className="text-2xl font-bold text-foreground">
                        {service.price1 ? `€ ${service.price1}` : "-"}
                      </span>
                    )}
                  </div>

                  {/* Availability Badge */}
                  {service.maxDailyQuantity != null &&
                    (() => {
                      const currentInCart = items
                        .filter(
                          (i) => String(i.serviceId) === String(service.id),
                        )
                        .reduce((acc, i) => acc + i.quantity, 0);
                      const bookedInDb = service.bookedDailyQuantities
                        ? Object.values(
                            service.bookedDailyQuantities as Record<
                              string,
                              number
                            >,
                          ).reduce((a, b) => a + b, 0)
                        : 0;
                      const remaining = Math.max(
                        0,
                        service.maxDailyQuantity - bookedInDb - currentInCart,
                      );
                      return (
                        <div className="flex items-center justify-between text-xs font-semibold px-3.5 py-2.5 rounded-xl border bg-muted/20">
                          <span className="text-muted-foreground">
                            Disponibilità:
                          </span>
                          {remaining > 10 ? (
                            <span className="text-emerald-500 font-semibold flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                              {remaining} posti disponibili
                            </span>
                          ) : remaining > 0 ? (
                            <span className="text-amber-500 font-semibold flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full bg-amber-500" />
                              Ultimi {remaining} posti
                            </span>
                          ) : (
                            <span className="text-destructive font-semibold flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full bg-destructive" />
                              Posti esauriti
                            </span>
                          )}
                        </div>
                      );
                    })()}

                  {/* Quantity controls */}
                  {service.serviceType === 1 && (
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-sm font-medium text-muted-foreground">
                        {t("number_pieces") || "Quantità"}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-9 w-9"
                          type="button"
                          onClick={() =>
                            setQuantityService((prev) => Math.max(prev - 1, 1))
                          }
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-8 text-center font-bold text-base">
                          {quantityService}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-9 w-9"
                          type="button"
                          disabled={(() => {
                            if (service.maxDailyQuantity == null) return false;
                            const currentInCart = items
                              .filter(
                                (i) =>
                                  String(i.serviceId) === String(service.id),
                              )
                              .reduce((acc, i) => acc + i.quantity, 0);
                            const bookedInDb = service.bookedDailyQuantities
                              ? Object.values(
                                  service.bookedDailyQuantities as Record<
                                    string,
                                    number
                                  >,
                                ).reduce((a, b) => a + b, 0)
                              : 0;
                            return (
                              currentInCart + bookedInDb + quantityService >
                              service.maxDailyQuantity
                            );
                          })()}
                          onClick={() => setQuantityService((prev) => prev + 1)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Add to cart */}
                  {service.serviceType != 2 && (
                    <Button
                      size="lg"
                      type="button"
                      disabled={(() => {
                        if (service.maxDailyQuantity == null) return false;
                        const currentInCart = items
                          .filter(
                            (i) => String(i.serviceId) === String(service.id),
                          )
                          .reduce((acc, i) => acc + i.quantity, 0);
                        const bookedInDb = service.bookedDailyQuantities
                          ? Object.values(
                              service.bookedDailyQuantities as Record<
                                string,
                                number
                              >,
                            ).reduce((a, b) => a + b, 0)
                          : 0;
                        return (
                          currentInCart + bookedInDb >= service.maxDailyQuantity
                        );
                      })()}
                      onClick={() => {
                        for (let i = 0; i < quantityService; i++) {
                          addItem({
                            id: String(service.id),
                            serviceId: String(service.id),
                            subServiceId: null,
                            name: service.name,
                            price: Number(service.offer || service.price1),
                            image: service.cover,
                            reservation: reservation?.reservation,
                            isQuantityFixed: service.serviceType === 0,
                            data: service.serviceAvailabilityData,
                            serviceAvailability: service.serviceAvailability,
                            maxDailyQuantity: service.maxDailyQuantity,
                            bookedDailyQuantities:
                              service.bookedDailyQuantities,
                          });
                        }
                      }}
                      className="w-full gap-2 text-base font-semibold shadow-sm"
                    >
                      <ShoppingCart className="h-5 w-5" />
                      {t("add_cart") || "Aggiungi al carrello"}
                    </Button>
                  )}
                </div>
              </Card>
            )}

            {/* Information & Opening Hours Box */}
            <Card className="p-6 space-y-5 border border-border/60 shadow-sm min-w-0">
              <h3 className="font-semibold text-base border-b pb-3 flex items-center gap-2 text-foreground">
                <Info className="h-4 w-4 text-primary shrink-0" />
                {service.serviceAvailability == 0
                  ? t("always_open")
                  : t("info_and_hours") !== "info_and_hours"
                    ? t("info_and_hours")
                    : "Informazioni e Orari"}
              </h3>

              {/* Location */}
              {locationString && (
                <div className="flex items-start gap-3 text-sm">
                  <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground text-xs uppercase tracking-wider text-muted-foreground">
                      {t("location") || "Posizione"}
                    </p>
                    <p className="text-foreground mt-0.5">{locationString}</p>
                  </div>
                </div>
              )}

              {/* Price level indicator */}
              {service.serviceLevelPrice != null && (
                <div className="flex items-center justify-between text-sm pt-1">
                  <span className="text-muted-foreground">
                    {t("price_level") || "Fascia prezzo"}:
                  </span>
                  <span className="font-semibold tracking-wider">
                    {(() => {
                      const level = Number(service.serviceLevelPrice);
                      const filled = "€".repeat(
                        Math.min(Math.max(level, 1), 4),
                      );
                      const empty = "€".repeat(4 - filled.length);
                      return (
                        <>
                          <span className="text-foreground">{filled}</span>
                          <span className="text-muted-foreground/30">
                            {empty}
                          </span>
                        </>
                      );
                    })()}
                  </span>
                </div>
              )}

              {/* Opening hours */}
              {service.serviceAvailabilityData &&
                service.serviceAvailabilityData !== "" &&
                (() => {
                  try {
                    const slot = JSON.parse(service.serviceAvailabilityData)[0];
                    if (!slot) return null;

                    return (
                      <div className="space-y-3 pt-2">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          <Clock className="h-3.5 w-3.5 text-primary shrink-0" />
                          <span>
                            {t("opening_hours") !== "OPENING_HOURS" &&
                            t("opening_hours") !== "opening_hours"
                              ? t("opening_hours")
                              : "Orari di apertura"}
                          </span>
                        </div>
                        <div className="divide-y divide-border/40 text-sm">
                          {days.map(({ key, label }) => {
                            const isOpen =
                              slot[key] === true ||
                              service.serviceAvailability == 0;
                            const t1s = slot[`${key}StartTime`];
                            const t1e = slot[`${key}EndTime`];
                            const t2s = slot[`${key}SecondStartTime`];
                            const t2e = slot[`${key}SecondEndTime`];
                            const slot1 = t1s && t1e ? `${t1s} – ${t1e}` : null;
                            const slot2 = t2s && t2e ? `${t2s} – ${t2e}` : null;

                            return (
                              <div
                                key={key}
                                className="py-2 flex items-center justify-between gap-2"
                              >
                                <span className="font-medium text-foreground text-xs sm:text-sm">
                                  {label}
                                </span>
                                <div className="text-right">
                                  {!isOpen ? (
                                    <Badge
                                      variant="outline"
                                      className="text-xs bg-destructive/10 text-destructive border-destructive/20 font-normal"
                                    >
                                      {t("close") || "Chiuso"}
                                    </Badge>
                                  ) : slot1 ? (
                                    <span className="text-xs text-muted-foreground font-mono">
                                      {slot1}
                                      {slot2 ? ` / ${slot2}` : ""}
                                    </span>
                                  ) : (
                                    <Badge
                                      variant="outline"
                                      className="text-xs bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 font-normal"
                                    >
                                      {t("open") || "Aperto"}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  } catch {
                    return null;
                  }
                })()}
            </Card>
          </div>
        </div>
      </motion.div>

      <Toaster />
    </div>
  );
}
